import React, { useEffect, useState, useMemo } from "react";
import api from "../api";
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getPaginationRowModel,
  flexRender,
} from "@tanstack/react-table";
import { FiSearch } from "react-icons/fi";
import { useNavigate } from "react-router-dom";

import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

export default function Precatorios() {
  const [logs, setLogs] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    let intervalId;

    const fetchLogs = async () => {
      try {
        const res = await api.get("/AI");
        setLogs(res.data);
      } catch (error) {
        toast.error(
          "Erro ao carregar precatorios. Tente novamente mais tarde."
        );
        console.error("Erro ao buscar precatorios:", error);
      }
    };

    fetchLogs(); // chamada inicial

    intervalId = setInterval(() => {
      fetchLogs(); // chamada a cada 10 segundos
    }, 10000); // 10000ms = 10s

    return () => {
      clearInterval(intervalId); // limpa ao desmontar
    };
  }, []);

  const columns = useMemo(
    () => [
      {
        accessorKey: "user.email",
        header: "Upload feito por",
        cell: (info) => info.getValue(),
      },
      {
        accessorKey: "createdAt",
        header: "Upload em",
        cell: (info) => new Date(info.getValue()).toLocaleString(),
      },
      {
        accessorKey: "updatedAt",
        header: "Atualizado em",
        cell: (info) => new Date(info.getValue()).toLocaleString(),
      },
      {
        accessorKey: "stage",
        header: "Etapa",
        cell: (info) => info.getValue(),
      },
      {
        accessorKey: "status",
        header: "Status",
        cell: (info) => info.getValue(),
      },
      {
        accessorKey: "nameFile",
        header: "Nome do Arquivo",
        cell: (info) => info.getValue(),
      },
      {
        accessorKey: "response",
        header: "Número do Precatório",
        cell: (info) => {
          const raw = info.getValue();
          try {
            const json = JSON.parse(
              typeof raw === "string"
                ? raw.replace(/```json|```/g, "").trim()
                : raw
            );
            return (
              json?.numeroProcessoPrecatório ||
              json?.numeroProcessoPrincipal ||
              "-"
            );
          } catch {
            return "-";
          }
        },
      },
      {
        id: "detalhes",
        header: "Detalhes",
        cell: ({ row }) => (
          <div style={{ textAlign: "center" }}>
            <button
              style={{
                background: "transparent",
                border: "none",
                cursor: "pointer",
                color: "#4b5c8a",
                fontSize: "1.2rem",
              }}
              onClick={() => {
                if (
                  !row.original.stage ||
                  row.original.stage.endsWith("Jurídica")
                ) {
                  navigate(`/detailsAnaliseJuridica/${row.original.id}`);
                } else {
                  navigate(`/detailsAnaliseFinanceira/${row.original.id}`);
                }
              }}
              aria-label="Ver detalhes"
            >
              <FiSearch />
            </button>
          </div>
        ),
      },
    ],
    []
  );

  const table = useReactTable({
    data: logs,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    initialState: { pagination: { pageSize: 15 } },
  });

  const oneLineStyle = {
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    maxWidth: "300px",
  };

  return (
    <div>
      <div style={{ flex: "1 0 auto", padding: "20px" }}>
        <table
          style={{
            flex: 1,
            width: "100%",
            background: "transparent",
            borderRadius: 8,
          }}
        >
          <thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <th
                    key={header.id}
                    style={{
                      padding: "10px 12px",
                      cursor: header.column.getCanSort()
                        ? "pointer"
                        : "default",
                      color: "#07335A",
                      textAlign: "left",
                      userSelect: "none",
                      maxWidth: "200px",
                      borderBottom: "none",
                    }}
                    onClick={header.column.getToggleSortingHandler()}
                  >
                    {flexRender(
                      header.column.columnDef.header,
                      header.getContext()
                    )}
                    {{
                      asc: " 🔼",
                      desc: " 🔽",
                    }[header.column.getIsSorted()] ?? null}
                  </th>
                ))}
              </tr>
            ))}
          </thead>

          <tbody>
            {table.getRowModel().rows.length === 0 && (
              <tr>
                <td
                  colSpan={columns.length}
                  style={{
                    textAlign: "center",
                    padding: 20,
                    color: "#666",
                  }}
                >
                  Nenhum log disponível.
                </td>
              </tr>
            )}
            {table.getRowModel().rows.map((row) => (
              <tr
                key={row.id}
                style={{
                  borderBottom: "none",
                  backgroundColor: "transparent",
                }}
              >
                {row.getVisibleCells().map((cell) => (
                  <td
                    key={cell.id}
                    style={{
                      padding: "8px 12px",
                      verticalAlign: "top",
                      ...oneLineStyle,
                      borderBottom: "none",
                    }}
                    title={cell.getValue()}
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Paginação fixa no rodapé */}
      <div
        style={{
          flexShrink: 0,
          padding: "16px 24px",
          borderTop: "1px solid #ddd",
          backgroundColor: "transparent",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          color: "#07335A",
        }}
      >
        <button
          onClick={() => table.previousPage()}
          disabled={!table.getCanPreviousPage()}
          style={{
            cursor: table.getCanPreviousPage() ? "pointer" : "not-allowed",
            background: "none",
            border: "none",
            color: "#07335A",
            fontSize: 14,
            padding: 6,
          }}
        >
          {"< Anterior"}
        </button>

        <span style={{ fontSize: 14 }}>
          Página{" "}
          <strong>
            {table.getState().pagination.pageIndex + 1} de{" "}
            {table.getPageCount()}
          </strong>
        </span>

        <button
          onClick={() => table.nextPage()}
          disabled={!table.getCanNextPage()}
          style={{
            cursor: table.getCanNextPage() ? "pointer" : "not-allowed",
            background: "none",
            border: "none",
            color: "#07335A",
            fontSize: 14,
            padding: 6,
          }}
        >
          {"Próximo >"}
        </button>
      </div>
    </div>
  );
}
